import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import sharepointService from '../../../services/sharepointService';
import microsoftGraphService from '../../../services/microsoftGraphService';
import { PublicClientApplication } from '@azure/msal-browser';

const SharePointUploadModal = ({ isOpen, onClose, document, onUploadSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [step, setStep] = useState('auth'); // 'auth', 'site', 'library', 'upload', 'complete'
  
  // SharePoint configuration
  const [sites, setSites] = useState([]);
  const [selectedSite, setSelectedSite] = useState(null);
  const [libraries, setLibraries] = useState([]);
  const [selectedLibrary, setSelectedLibrary] = useState(null);
  const [folderPath, setFolderPath] = useState('ZentriDMS');
  const [uploadedFile, setUploadedFile] = useState(null);

  // MSAL configuration
  const msalConfig = {
    auth: {
      clientId: import.meta.env?.VITE_MICROSOFT_CLIENT_ID,
      authority: import.meta.env?.VITE_MICROSOFT_AUTHORITY,
      redirectUri: import.meta.env?.VITE_MICROSOFT_REDIRECT_URI,
    },
  };

  const msalInstance = new PublicClientApplication(msalConfig);

  useEffect(() => {
    if (isOpen) {
      initializeModal();
    }
  }, [isOpen]);

  const initializeModal = () => {
    setStep('auth');
    setError('');
    setSuccess(false);
    setUploadedFile(null);
  };

  const handleAuthenticate = async () => {
    try {
      setLoading(true);
      setError('');

      // Request SharePoint permissions
      const scopes = import.meta.env?.VITE_MICROSOFT_SCOPES?.split(',') || [
        'User.Read',
        'Files.ReadWrite.All',
        'Sites.ReadWrite.All',
      ];

      const loginResponse = await msalInstance?.loginPopup({
        scopes,
      });

      const accessToken = loginResponse?.accessToken;
      
      // Set token in both services
      sharepointService?.setAccessToken(accessToken);
      microsoftGraphService?.setAccessToken(accessToken);

      // Move to site selection
      setStep('site');
      await loadSharePointSites();
    } catch (err) {
      console.error('Authentication error:', err);
      setError('Failed to authenticate with Microsoft. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const loadSharePointSites = async () => {
    try {
      setLoading(true);
      const userSites = await sharepointService?.getUserSites();
      setSites(userSites);
      
      if (userSites?.length === 0) {
        setError('No SharePoint sites found. Please contact your administrator.');
      }
    } catch (err) {
      console.error('Error loading sites:', err);
      setError('Failed to load SharePoint sites: ' + err?.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSiteSelect = async (site) => {
    try {
      setLoading(true);
      setError('');
      setSelectedSite(site);
      sharepointService?.setSiteId(site?.id);

      // Load document libraries for selected site
      const siteLibraries = await sharepointService?.getDocumentLibraries(site?.id);
      setLibraries(siteLibraries);
      setStep('library');
    } catch (err) {
      console.error('Error loading libraries:', err);
      setError('Failed to load document libraries: ' + err?.message);
    } finally {
      setLoading(false);
    }
  };

  const handleLibrarySelect = (library) => {
    setSelectedLibrary(library);
    sharepointService?.setDriveId(library?.id);
    setStep('upload');
  };

  const handleUpload = async () => {
    if (!document) {
      setError('No document selected');
      return;
    }

    try {
      setLoading(true);
      setError('');

      // Fetch document file from Supabase
      const response = await fetch(document?.storage_path);
      const blob = await response?.blob();
      const file = new File([blob], document?.name, { type: document?.type });

      // Upload to SharePoint
      const result = await sharepointService?.uploadDocument(
        file,
        document?.name,
        folderPath
      );

      setUploadedFile(result);
      setSuccess(true);
      setStep('complete');

      // Notify parent component
      if (onUploadSuccess) {
        onUploadSuccess(result);
      }
    } catch (err) {
      console.error('Upload error:', err);
      setError('Failed to upload document: ' + err?.message);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenInSharePoint = () => {
    if (uploadedFile?.editUrl) {
      window.open(uploadedFile?.editUrl, '_blank');
    }
  };

  // NEW: Force close modal - overrides all states
  const forceCloseModal = (e) => {
    if (e) {
      e?.preventDefault();
      e?.stopPropagation();
    }
    // Reset all states
    setStep('auth');
    setError('');
    setSuccess(false);
    setLoading(false);
    setUploadedFile(null);
    setSites([]);
    setLibraries([]);
    setSelectedSite(null);
    setSelectedLibrary(null);
    // Call parent onClose
    onClose();
  };

  // ESC key handler
  useEffect(() => {
    const handleEscKey = (e) => {
      if (e?.key === 'Escape' && isOpen) {
        forceCloseModal();
      }
    };

    if (isOpen) {
      document?.addEventListener('keydown', handleEscKey);
      return () => document?.removeEventListener('keydown', handleEscKey);
    }
  }, [isOpen]);

  const handleBackdropClick = (e) => {
    // Only close if clicking exactly on the backdrop, not children
    if (e?.target === e?.currentTarget) {
      forceCloseModal(e);
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={handleBackdropClick}
    >
      <div 
        className="bg-card border border-border rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e?.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
              <Icon name="Upload" size={20} className="text-primary" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-foreground">
                Upload to SharePoint
              </h2>
              <p className="text-sm text-muted-foreground">
                Connect to SharePoint for collaborative editing
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={forceCloseModal}
            className="text-muted-foreground hover:text-foreground p-2 rounded-lg hover:bg-muted transition-colors"
            aria-label="Close modal"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Error Message */}
          {error && (
            <div className="mb-6 bg-destructive/10 border border-destructive/20 text-destructive px-4 py-3 rounded-lg">
              <div className="flex items-center gap-2">
                <Icon name="AlertTriangle" size={16} />
                <span className="text-sm">{error}</span>
                <button
                  onClick={() => setError('')}
                  className="ml-auto hover:bg-destructive/20 rounded p-1"
                >
                  <Icon name="X" size={14} />
                </button>
              </div>
            </div>
          )}

          {/* Success Message */}
          {success && (
            <div className="mb-6 bg-green-500/10 border border-green-500/20 text-green-500 px-4 py-3 rounded-lg">
              <div className="flex items-center gap-2">
                <Icon name="CheckCircle" size={16} />
                <span className="text-sm">Document uploaded successfully!</span>
              </div>
            </div>
          )}

          {/* Step: Authentication */}
          {step === 'auth' && (
            <div className="text-center py-8">
              <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center mb-4 mx-auto">
                <Icon name="Shield" size={32} className="text-primary" />
              </div>
              <h3 className="text-lg font-semibold text-foreground mb-2">
                Connect to Microsoft 365
              </h3>
              <p className="text-muted-foreground mb-6">
                Sign in to access your SharePoint sites and document libraries
              </p>
              <button
                type="button"
                onClick={handleAuthenticate}
                disabled={loading}
                className="flex items-center gap-2 px-6 py-3 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 disabled:opacity-50 mx-auto"
              >
                {loading ? (
                  <>
                    <Icon name="Loader" size={16} className="animate-spin" />
                    <span>Connecting...</span>
                  </>
                ) : (
                  <>
                    <Icon name="LogIn" size={16} />
                    <span>Sign in with Microsoft</span>
                  </>
                )}
              </button>
            </div>
          )}

          {/* Step: Site Selection */}
          {step === 'site' && (
            <div>
              <h3 className="text-lg font-semibold text-foreground mb-4">
                Select SharePoint Site
              </h3>
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <Icon name="Loader" size={24} className="animate-spin text-primary" />
                </div>
              ) : sites?.length > 0 ? (
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {sites?.map((site) => (
                    <div
                      key={site?.id}
                      onClick={() => handleSiteSelect(site)}
                      className="flex items-center gap-4 p-4 border border-border rounded-lg hover:bg-muted cursor-pointer transition-colors"
                    >
                      <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                        <Icon name="Globe" size={20} className="text-primary" />
                      </div>
                      <div className="flex-1">
                        <h4 className="font-medium text-foreground">{site?.displayName}</h4>
                        <p className="text-sm text-muted-foreground">{site?.webUrl}</p>
                      </div>
                      <Icon name="ChevronRight" size={16} className="text-muted-foreground" />
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <p className="text-muted-foreground">No SharePoint sites available</p>
                </div>
              )}
            </div>
          )}

          {/* Step: Library Selection */}
          {step === 'library' && (
            <div>
              <div className="flex items-center gap-2 mb-4">
                <button
                  type="button"
                  onClick={() => setStep('site')}
                  className="text-muted-foreground hover:text-foreground"
                >
                  <Icon name="ChevronLeft" size={16} />
                </button>
                <h3 className="text-lg font-semibold text-foreground">
                  Select Document Library
                </h3>
              </div>
              <p className="text-sm text-muted-foreground mb-4">
                Site: {selectedSite?.displayName}
              </p>
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <Icon name="Loader" size={24} className="animate-spin text-primary" />
                </div>
              ) : libraries?.length > 0 ? (
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {libraries?.map((library) => (
                    <div
                      key={library?.id}
                      onClick={() => handleLibrarySelect(library)}
                      className="flex items-center gap-4 p-4 border border-border rounded-lg hover:bg-muted cursor-pointer transition-colors"
                    >
                      <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                        <Icon name="FolderOpen" size={20} className="text-primary" />
                      </div>
                      <div className="flex-1">
                        <h4 className="font-medium text-foreground">{library?.name}</h4>
                        <p className="text-sm text-muted-foreground">
                          {library?.description || 'SharePoint document library'}
                        </p>
                      </div>
                      <Icon name="ChevronRight" size={16} className="text-muted-foreground" />
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <p className="text-muted-foreground">No document libraries found</p>
                </div>
              )}
            </div>
          )}

          {/* Step: Upload Configuration */}
          {step === 'upload' && (
            <div>
              <div className="flex items-center gap-2 mb-4">
                <button
                  type="button"
                  onClick={() => setStep('library')}
                  className="text-muted-foreground hover:text-foreground"
                >
                  <Icon name="ChevronLeft" size={16} />
                </button>
                <h3 className="text-lg font-semibold text-foreground">
                  Upload Configuration
                </h3>
              </div>

              {/* Document Info */}
              <div className="bg-muted rounded-lg p-4 mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                    <Icon name="FileText" size={24} className="text-primary" />
                  </div>
                  <div>
                    <h4 className="font-medium text-foreground">{document?.name}</h4>
                    <p className="text-sm text-muted-foreground">
                      {document?.size ? `${Math.round(document?.size / 1024)} KB` : 'Unknown size'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Upload Configuration */}
              <div className="space-y-4 mb-6">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Destination Folder Path
                  </label>
                  <input
                    type="text"
                    value={folderPath}
                    onChange={(e) => setFolderPath(e?.target?.value)}
                    placeholder="e.g., ZentriDMS/2024/Documents"
                    className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Leave empty to upload to root folder
                  </p>
                </div>

                <div className="bg-muted/50 rounded-lg p-4">
                  <p className="text-sm text-foreground mb-2">
                    <span className="font-medium">Site:</span> {selectedSite?.displayName}
                  </p>
                  <p className="text-sm text-foreground">
                    <span className="font-medium">Library:</span> {selectedLibrary?.name}
                  </p>
                </div>
              </div>

              {/* Upload Button */}
              <div className="flex gap-3 justify-end">
                <button
                  type="button"
                  onClick={() => setStep('library')}
                  className="px-4 py-2 border border-border rounded-lg text-muted-foreground hover:bg-muted"
                >
                  Back
                </button>
                <button
                  type="button"
                  onClick={handleUpload}
                  disabled={loading}
                  className="flex items-center gap-2 px-6 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 disabled:opacity-50"
                >
                  {loading ? (
                    <>
                      <Icon name="Loader" size={16} className="animate-spin" />
                      <span>Uploading...</span>
                    </>
                  ) : (
                    <>
                      <Icon name="Upload" size={16} />
                      <span>Upload to SharePoint</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          )}

          {/* Step: Complete */}
          {step === 'complete' && (
            <div className="text-center py-8">
              <div className="w-20 h-20 bg-green-500/10 rounded-full flex items-center justify-center mb-4 mx-auto">
                <Icon name="CheckCircle" size={32} className="text-green-500" />
              </div>
              <h3 className="text-lg font-semibold text-foreground mb-2">
                Upload Successful!
              </h3>
              <p className="text-muted-foreground mb-6">
                Your document is now available in SharePoint and ready for collaborative editing
              </p>

              <div className="bg-muted rounded-lg p-4 mb-6 text-left">
                <div className="space-y-2">
                  <div className="flex items-start gap-2">
                    <Icon name="CheckCircle" size={16} className="text-green-500 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-foreground">Document Location</p>
                      <p className="text-xs text-muted-foreground break-all">
                        {selectedSite?.displayName} → {selectedLibrary?.name} → {folderPath || 'Root'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <Icon name="CheckCircle" size={16} className="text-green-500 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-foreground">Ready for Editing</p>
                      <p className="text-xs text-muted-foreground">
                        Open in SharePoint to edit with Office Online
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex gap-3 justify-center">
                <button
                  type="button"
                  onClick={forceCloseModal}
                  className="px-4 py-2 border border-border rounded-lg text-muted-foreground hover:bg-muted"
                >
                  Close
                </button>
                <button
                  type="button"
                  onClick={handleOpenInSharePoint}
                  className="flex items-center gap-2 px-6 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90"
                >
                  <Icon name="ExternalLink" size={16} />
                  <span>Open in SharePoint</span>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SharePointUploadModal;